import React, { useState } from "react"
import styled from "styled-components"
import colors from "../../../styles/colors"
import fonts from "../../../styles/fonts"
import breakpoints from "../../../styles/breakpoints"
import { Trans, withTranslation } from "gatsby-plugin-react-i18next"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { Link } from "gatsby"
import DiamondIcon from "../../../resources/img/pdp/pdp-diamond.svg"
import GoldIcon from "../../../resources/img/pdp/pdp-gold.svg"
import CordIcon from "../../../resources/img/pdp/pdp-cord.svg"
import { getProductTags } from "../../../services/productHelper"
import { getIsFrance } from "../../../services/locale"

let activeEnv = process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || "us"

if (activeEnv === "staging" || activeEnv === "development") {
  activeEnv = "us"
}

const ColumnsContainer = styled.div`
  ${fonts.openSans};
  font-size: 12px;
  line-height: 1.83;
  padding: 0 13px;
  margin: 26px auto 0;

  @media (min-width: ${breakpoints.lg}) {
    font-size: 14px;
    padding: 0;
    margin: 26px auto;
  }
`

const ProductInfo = styled.div`
  border-top: 1px solid ${colors.borderLightGrey};
  border-bottom: 1px solid ${colors.borderLightGrey};
  padding: 16px 0;

  a {
    color: ${colors.green};
  }

  p {
    margin-bottom: 16px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }

  @media (min-width: ${breakpoints.lg}) {
    font-size: 16px;
    padding: 26px 0;
  }
`

const Properties = styled.ul`
  padding: 20px 0 0;
`

const PropertyTab = styled.li`
  margin: 20px 0;
  border-top: 1px solid ${colors.borderLightGrey};
  padding-top: 20px;
  &:first-of-type {
    border-top: none;
    margin-top: 0;
    padding-top: 0;
  }
`

const PropertyTitle = styled.div`
  font-size: 12px;
  line-height: 1.17;
  font-weight: 700;
  letter-spacing: 0.85px;
  // text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  @media (min-width: ${breakpoints.lg}) {
    font-size: 14px;
  }
`

const Drawer = styled.div`
  height: ${(props) => (props.open ? "100%" : "0")};
  overflow: hidden;
`

const PropertyText = styled.div`
  padding: 12px 0;
  margin-bottom: 16px;

  a {
    color: inherit;
  }
`

const Material = styled.div`
  margin: 26px 0;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const MaterialTitle = styled.div`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  margin: 2px 0;
  font-weight: 600;
  img {
    padding-right: 10px;
  }

  @media (min-width: ${breakpoints.lg}) {
    font-size: 14px;
  }
`

const FreeText = styled.div`
  p {
    margin-bottom: 16px;
  }

  a {
    color: ${colors.green};
  }

  b {
    font-weight: 700;
  }

  ul {
    list-style: disc;
    padding-left: 20px;
    margin-bottom: 4px;
    li {
      p {
        margin: 0;
      }
    }
  }
`

const Toggle = styled.div``

const InfoColumns = ({ product, description, content, title, t }) => {
  const { handle, options } = product
  const {
    numberOfDiamonds,
    carats,
    goldWeight,
    necklaceLengths,
    sizeFit,
    labGrownDiamondsText,
    recycledGoldText,
    sizeAndFit,
    noReturnsResizable,
    shapeOfDiamonds,
    diamondColor,
    diamondClarity,
  } = content

  const [openedTabs, setOpenedTabs] = useState([])
  const isFrance = getIsFrance()

  const productTags = getProductTags(product, content)

  const productCategory = product.productType.toLowerCase().includes("necklace")
    ? "necklaces"
    : product.productType.toLowerCase().includes("bracelet")
      ? "bracelets"
      : product.productType.toLowerCase().includes("earring")
        ? "earrings"
        : product.productType.toLowerCase().includes("ring")
          ? "rings"
          : product.productType.toLowerCase()

  const isWeddingBand = productCategory === "wedding band"
  let isPersonalized = false
  let isEngraving =
    isWeddingBand &&
    product.handle !== "stellar-band" &&
    product.handle !== "stella-ring"
      ? true
      : false
  const isRing = productCategory === "rings" || isWeddingBand
  let engravingLimit = 15 //Used for ring's engraving instructions only. 15 is default limit for rings

  const activeEnv = process.env.GATSBY_ACTIVE_ENV || "us"

  let isArchiveProduct =
    productTags.includes("winter-archives") ||
    productTags.includes("noreturn-archive")

  let isLastChanceProduct = productTags.includes("winter-archives")

  productTags.forEach((tag) => {
    const tagString = tag.toLowerCase()

    if (tagString === "personalized" || tagString === "customized") {
      isPersonalized = true
    }
    if (tag.includes("Engraving")) {
      isEngraving = true
      engravingLimit = tag.replace(/[^0-9]/g, "")
    }
  })

  let diamondsText = ""
  let goldText = ""
  let chainText = ""
  let necklaceText = ""
  let braceletText = ""
  let returnWeddingText = t("pdp.return_wedding_no_diamond")
  let shape = t("pdp.shape.brilliant")
  const shapes = shapeOfDiamonds ? [...shapeOfDiamonds] : []

  if (shapeOfDiamonds && shapeOfDiamonds.length > 0) {
    if (shapeOfDiamonds.length === 1) {
      shape = shapeOfDiamonds[0]
    } else {
      shape = shapeOfDiamonds
        .map((shape, index) => {
          if (index === 0) {
            return shape
          } else {
            return index === shapeOfDiamonds.length - 1
              ? ` ${t("pdp.and")} ` + shape
              : ", " + shape
          }
        })
        .join("")
    }
  }

  if (numberOfDiamonds && numberOfDiamonds > 0) {
    returnWeddingText = t("pdp.return_wedding_diamond")
    if (numberOfDiamonds > 1) {
      if (shapes.length <= 1) {
        diamondsText = (
          <Trans i18nKey={"pdp.diamonds_number_plural"}>
            The {title} is set with {numberOfDiamonds} {shape}-cut diamonds
          </Trans>
        )
      } else {
        diamondsText = (
          <Trans i18nKey={"pdp.diamonds_multiple_shapes"}>
            The {title} is set with {shape} diamonds
          </Trans>
        )
      }
    } else {
      diamondsText = (
        <Trans i18nKey={"pdp.diamonds_number"}>
          The {title} is set with {shape}-cut diamond
        </Trans>
      )
    }

    if (carats) {
      diamondsText = (
        <>
          {diamondsText}
          <Trans i18nKey={"pdp.carats_number"} count={carats}>
            totalling {carats} carat.
          </Trans>
        </>
      )
    } else {
      diamondsText = <>{diamondsText}.</>
    }

    if (diamondColor && diamondClarity) {
      const diamond = `diamond${
        numberOfDiamonds && numberOfDiamonds > 1 ? "s" : ""
      }`
      const verb = numberOfDiamonds && numberOfDiamonds > 1 ? "have" : "has"
      const verbFr = numberOfDiamonds && numberOfDiamonds > 1 ? "sont" : "est"
      const articleFr = numberOfDiamonds && numberOfDiamonds > 1 ? "Les" : "Le"

      diamondsText = (
        <>
          {diamondsText}
          <Trans
            i18nKey="pdp.color_clarity"
            values={{
              articleFr: articleFr,
              diamond: diamond,
              verb: isFrance ? verbFr : verb,
              color: diamondColor,
              clarity: diamondClarity,
            }}
          >
            The {diamond} {verb} a color grade of {diamondColor} and clarity{" "}
            {diamondClarity}.
          </Trans>
        </>
      )
    }
  } else if (carats) {
    if (carats > 1) {
      diamondsText = (
        <Trans i18nKey={"pdp.totalling_carats_number_plural"}>
          The {title} is totalling {carats} carats.
        </Trans>
      )
    } else {
      diamondsText = (
        <Trans i18nKey={"pdp.totalling_carats_number"}>
          The {title} is totalling {carats} carat.
        </Trans>
      )
    }
  }

  if (goldWeight) {
    goldText = (
      <Trans i18nKey={"pdp.gold_weight"}>
        This piece has a total of {goldWeight}g of gold
      </Trans>
    )
  }

  if (
    productCategory === "necklaces" ||
    productCategory === "bracelets" ||
    handle === "brilliant-earring"
  ) {
    chainText = t("pdp.gold_includes_chain")
  } else {
    chainText = ""
  }

  if (productCategory === "necklaces" && necklaceLengths) {
    if (necklaceLengths.length > 1) {
      necklaceText = t("pdp.necklace.different_lengths")
      let sizeCount = necklaceLengths.length

      for (let i = 0; i < sizeCount; i++) {
        let length = necklaceLengths[i] + "cm"

        if (i === 0) {
          necklaceText = necklaceText + " " + length
        } else if (i === sizeCount - 1) {
          necklaceText = necklaceText + t("pdp.and") + length
        } else {
          necklaceText = necklaceText + " , " + length
        }
      }
    } else {
      necklaceText =
        t("pdp.necklace.different_lengths") + necklaceLengths[0] + "cm"
    }

    necklaceText = necklaceText + ". "
  }

  if (productCategory === "bracelets" && necklaceLengths) {
    if (necklaceLengths.length > 1) {
      braceletText = t("pdp.bracelet.different_lengths")
      let sizeCount = necklaceLengths.length

      for (let i = 0; i < sizeCount; i++) {
        let length = necklaceLengths[i] + "cm"

        if (i === 0) {
          braceletText = braceletText + " " + length
        } else if (i === sizeCount - 1) {
          braceletText = braceletText + t("pdp.and") + length
        } else {
          braceletText = braceletText + " , " + length
        }
      }
    } else {
      braceletText = t("pdp.bracelet.length") + necklaceLengths[0] + "cm"
    }

    braceletText = braceletText + ". "
  }

  let earringsText
  if (productCategory === "earrings") {
    let hasLeft = false

    options.forEach((option) => {
      const optionValues = option.values

      if (optionValues.includes("Left") || optionValues.includes("left")) {
        hasLeft = true
      }
    })

    if (hasLeft) {
      earringsText = t("pdp.earring_fits_one_ear")
    } else {
      earringsText = t("pdp.earring.sold_per_unit")
    }
  }

  let properties = [
    {
      title: "Materials",
      text: (
        <>
          {labGrownDiamondsText ? (
            <>
              <FreeText>{renderRichText(labGrownDiamondsText)}</FreeText>
            </>
          ) : (
            <>{diamondsText}</>
          )}
          {(diamondsText || labGrownDiamondsText) && (
            <>
              <br />
            </>
          )}
          {recycledGoldText ? (
            <>
              <FreeText>{renderRichText(recycledGoldText)}</FreeText>
            </>
          ) : (
            goldText && (
              <>
                {goldText}
                {chainText ? ` ${chainText}.` : ""}
              </>
            )
          )}
          {numberOfDiamonds !== "0" && (
            <Material>
              <MaterialTitle>
                <img src={DiamondIcon} alt="gold" width="31" height="22" />
                {t("pdp.info.material.lab_diamonds_title")}
              </MaterialTitle>
              <p>
                <Trans i18nKey="pdp.info.material.lab_diamonds_text">
                  Our diamonds are created using cutting-edge technology that
                  replicates natural growing conditions in a lab. This results
                  in diamonds that are chemically and physically identical to
                  the ones sourced from mines.{" "}
                  {activeEnv === "us" ? (
                    <Link to="/learn/lab-grown-diamonds/">Learn more here</Link>
                  ) : (
                    <a
                      href="https://kimai.com/learn/lab-grown-diamonds/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more here
                    </a>
                  )}
                  .
                </Trans>
              </p>
            </Material>
          )}
          <Material>
            <MaterialTitle>
              <img src={GoldIcon} alt="gold" width="34" height="22" />
              {t("pdp.info.material.gold_title")}
            </MaterialTitle>
            <p>
              {product.handle.includes("-men-") &&
              productCategory === "bracelets" ? (
                <Trans i18nKey="pdp.info.material.gold_text_men">
                  Gold can be melted and reshaped without losing its quality. By
                  using recycled gold, we cut out the unsustainable mining
                  process of new gold. 18k gold is the most typical type used
                  for high quality luxury fine jewelry. It has a desirable,
                  sunny yellow appearance that is deemed classically attractive.{" "}
                  {activeEnv === "us" ? (
                    <Link to="/learn/recycled-gold/">Learn more here</Link>
                  ) : (
                    <a
                      href="https://kimai.com/learn/recycled-gold/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more here
                    </a>
                  )}
                </Trans>
              ) : (
                <Trans i18nKey="pdp.info.material.gold_text">
                  Gold can be melted and reshaped without losing its quality. By
                  using recycled gold, we cut out the unsustainable mining
                  process of new gold. 18k gold is the most typical type used
                  for high quality luxury fine jewelry. It has a desirable,
                  sunny yellow appearance that is deemed classically attractive.{" "}
                  {activeEnv === "us" ? (
                    <Link to="/learn/recycled-gold/">Learn more here</Link>
                  ) : (
                    <a
                      href="https://kimai.com/learn/recycled-gold/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more here
                    </a>
                  )}
                </Trans>
              )}
            </p>
          </Material>
          {(product.handle.includes("-men-") ||
            product.handle.includes("amie-rainbow")) &&
            productCategory === "bracelets" && (
              <Material>
                <MaterialTitle>
                  <img src={CordIcon} alt="cord" width="33" height="23" />
                  {t("pdp.info.material.cord_title")}
                </MaterialTitle>
                <p>
                  <Trans i18nKey="pdp.info.material.cord_text">
                    Our bracelet cords are made from 100% recycled and fully
                    traceable plastic. Our cords began life as plastic recovered
                    from recycling bins in northern Italy. They were cleaned,
                    cut and converted into polyester mechanically, without
                    chemicals. Recycled polyester requires far less energy than
                    virgin, meaning a huge reduction in carbon emissions and
                    water consumption.{" "}
                  </Trans>
                </p>
              </Material>
            )}
        </>
      ),
      visible: handle !== "unity-men-bracelet",
    },

    {
      title: t("pdp.dropdown.engraving"),
      text: (
        <div>
          <FreeText>
            <ul>
              {isRing ? (
                <>
                  <li>{t("pdp.info.rings.01")}</li>
                  <li>
                    <Trans
                      i18nKey="pdp.info.rings.02"
                      values={{
                        engravingLimit: engravingLimit,
                      }}
                    >
                      The engraving can be up to {engravingLimit} characters.
                      Symbols should be enclosed in asterisks for our design
                      team to understand.
                    </Trans>
                  </li>
                </>
              ) : (
                <>
                  {product.handle === "memory-bracelet" ? (
                    <li>{t("pdp.info.engraving_instruction.01")}</li>
                  ) : null}
                  <li>{t("pdp.info.engraving_instruction.02")}</li>
                  <li>{t("pdp.info.engraving_instruction.03")}</li>
                  <li>{t("pdp.info.engraving_instruction.04")}</li>
                </>
              )}
            </ul>
          </FreeText>
        </div>
      ),
      visible:
        isEngraving &&
        !product.handle.includes("galactic") &&
        !product.handle.includes("momento")
          ? true
          : false,
    },
    {
      title: t("pdp.dropdown.size"),
      text: (
        <div>
          {product.handle === "eclipse-pear-bangle" ||
          product.handle === "eclipse-bangle" ? (
            <FreeText>
              {renderRichText(sizeAndFit)}
              <a
                href="https://kimai.com/guide/measure-your-wrist.pdf"
                target="_blank"
                rel="noreferrer"
              >
                {t("pdp.measure_wrist")}
              </a>
              <br />
              <a
                href="https://kimai.com/guide/bracelet-size-chart.pdf"
                target="_blank"
                rel="noreferrer"
              >
                {t("pdp.bracelet_size_chart")}
              </a>
            </FreeText>
          ) : isWeddingBand ? (
            <FreeText>
              {t("pdp.wb.size_fit")}
              <a href="mailto:info@kimai.com">info@kimai.com</a>.
            </FreeText>
          ) : sizeAndFit ? (
            <FreeText>{renderRichText(sizeAndFit)}</FreeText>
          ) : (
            <>
              {productCategory === "necklaces" && necklaceText}
              {productCategory === "earrings" && earringsText}
              {productCategory === "bracelets" && necklaceText}
              {sizeFit}
              {isRing && t("pdp.size_fit.not_sure")}
            </>
          )}
        </div>
      ),
      visible: true,
    },

    {
      title: t("pdp.holiday.last_chance.title"),
      text: <div>{t("pdp.holiday.last_chance.content")}</div>,
      visible: isLastChanceProduct ? true : false,
    },
    {
      title: isWeddingBand
        ? t("pdp.engraving_wedding_title")
        : t("pdp.dropdown.shipping"),
      text: isArchiveProduct ? (
        <div>{t("pdp.return_no_returns_archive")}</div>
      ) : isWeddingBand ? (
        <div>
          <Trans i18nKey="pdp.return_wedding_diamond">
            To ensure the perfect fit, we recommend confirming your size before
            ordering. If you're unsure, you can order a free ring sizer or reach
            out to us—we’re happy to help! While we don’t accept returns on
            rings, we offer a resizing service (1.5EU size up or down) for
            deliveries to the UK, EU, USA and UAE. For more information, contact
            us at <Link href="mailto:info@kimai.com">info@kimai.com</Link>.
          </Trans>
        </div>
      ) : (
        <div>
          {product.handle === "tennis-bracelet" ? (
            <Trans i18nKey="pdp.return_tennis_bracelet">
              As this piece is made to order, we can’t accept returns for this
              product. Unfortunately, resizing won’t be possible on that kind of
              bracelet. <Link to="/contact/">Contact us</Link> for more
              information.
            </Trans>
          ) : product.handle === "atlas-necklace" ||
            product.handle === "atlas-pave-necklace" ? (
            <Trans i18nKey="pdp.return_atlas_necklace">
              As this piece is made to order, we can’t accept returns for this
              product. That being said, we can resize it if needed to make it
              shorter or longer. <Link to="/contact/">Contact us</Link> for more
              information.
            </Trans>
          ) : product.handle === "salazar-bracelet" ? (
            <Trans i18nKey="pdp.return_salazar_bracelet">
              As this piece is made to order, we can’t accept returns for this
              product. However, the cord can be changed for another color.{" "}
              <Link to="/contact/">Contact us</Link> for more information.
            </Trans>
          ) : product.handle === "layla-earring" ||
            product.handle === "splendido-earrings" ? (
            <Trans i18nKey="pdp.return_dolla_splendido">
              As this piece is made to order, we can’t accept returns for this
              product. <Link to="/contact/">Contact us</Link> for more
              information.
            </Trans>
          ) : product.handle === "nava-tennis-necklace" ? (
            <Trans i18nKey="pdp.return_nava_necklace">
              As this piece is made to order, we can’t accept returns for this
              product. Due to the complexity of the design, resizing won’t be
              possible on that kind of necklace.{" "}
              <Link to="/contact/">Contact us</Link> for more information.
            </Trans>
          ) : product.handle === "unico-bracelet" ||
            product.handle === "harmony-necklace" ? (
            <Trans i18nKey="pdp.return_no_return_no_resize">
              As this piece is made to order, we can't accept returns or resize
              for this product so please make sure you order the right size.
            </Trans>
          ) : isPersonalized === true && noReturnsResizable !== true ? (
            t("pdp.return_personalized")
          ) : isRing ? (
            <Trans i18nKey="pdp.return_rings">
              As our rings are made to order, we can’t accept returns on the
              above piece. All purchases are therefore final. If your ring is
              not the correct size, we will be able to resize it for you (three
              sizes up or down). Learn more about our return policy
              <Link to={"/faq/"}>here</Link>.
            </Trans>
          ) : noReturnsResizable === true ? (
            <Trans i18nKey="pdp.return_no_returns_resizable">
              As this piece is made to order, we can’t accept returns for this
              product. However, we can resize it if needed.{" "}
              <Link to="/contact/">Contact us</Link> for more information.
            </Trans>
          ) : (
            <Trans i18nKey={"pdp.return_general"}>
              Conscious consuming is about being 100% sure. If you’re not, send
              it straight back within 14 days.
              <Link to="/contact/">Contact us</Link> for more information, or
              visit our page <Link to={"/faq/"}>Return & exchanges</Link>
            </Trans>
          )}
        </div>
      ),
      visible: true,
    },
    // {
    //   title: t("pdp.dropdown.holiday"),
    //   text: (
    //     <div>
    //       <FreeText>
    //         <p>
    //         {t("pdp.dropdown.textholiday1")}
    //         </p>
    //         <p>
    //         {t("pdp.dropdown.textholiday2")}
    //         </p>
    //         <p dangerouslySetInnerHTML={{
    //           __html: t("pdp.dropdown.textholiday3"),
    //         }} />
    //       </FreeText>
    //     </div>
    //   ),
    //   visible:
    //   !isUS &&
    //   (productCategory !== "rings" && ![
    //     'nava-tennis-necklace',
    //     'musa-bracelet',
    //     'eclipse-bangle',
    //     'eclipse-pear-bangle',
    //     'atlas-pave-necklace',
    //     'atlas-necklace',
    //     'unico-bracelet',
    //     'tennis-bracelet',
    //     'harmony-necklace',
    //     'milestone-necklace',
    //     'stellar-band',
    //     'stella-ring',
    //     'castle-eternity',
    //     'salazar-bracelet',
    //     'marquise-eternity',
    //     'unico-bracelet',
    //     'noma-letter-bracelet',
    //     'memory-bracelet',
    //     'legato-earring',
    //     'nocturne-choker-necklace',
    //     'stellar-hoop-earring',
    //     'stellar-ear-cuff',
    //     'twin-star-hoop',
    //     'ada-3ct-ring',
    //     'daisy-earring',
    //     'dilly-bangle',
    //     'galactic-bangle',
    //   ].includes(product.handle)),
    // },
  ]

  const toggleTabs = (tabIndex) => {
    let newOpenedTabs = []
    if (openedTabs.includes(tabIndex)) {
      newOpenedTabs = openedTabs.filter((tab) => tab !== tabIndex)
    } else {
      newOpenedTabs = [...openedTabs, tabIndex]
    }

    setOpenedTabs(newOpenedTabs)
  }

  return (
    <ColumnsContainer>
      <ProductInfo dangerouslySetInnerHTML={{ __html: description }} />
      <Properties>
        {properties.map((property, index) => {
          const isOpen = openedTabs.includes(index)
          if (!property.visible) return null
          return (
            <PropertyTab key={property.title}>
              <PropertyTitle onClick={() => toggleTabs(index)}>
                {property.title}
                <Toggle>{!isOpen ? "+" : "–"}</Toggle>
              </PropertyTitle>
              <Drawer open={isOpen ? 1 : 0}>
                <PropertyText>{property.text}</PropertyText>
              </Drawer>
            </PropertyTab>
          )
        })}
      </Properties>
    </ColumnsContainer>
  )
}

export default withTranslation()(InfoColumns)
